"use client";

import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import type React from "react";
import { useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { $api } from "@/lib/api-client";
import { ProductSearchResults } from "./ProductSearchResults";
import { SearchResultSkeleton } from "./SearchResultSkeleton";
import {
  SEARCH_MIN_CHARS,
  SEARCH_MOTION_TRANSITION,
  SEARCH_OVERLAY_VARIANTS,
} from "./search-config";
import { VendorSearchResults } from "./VendorSearchResults";

interface SearchResultsOverlayProps {
  isVisible: boolean;
  searchValue: string;
  navHeight: number;
  onResultClick: () => void;
}

export const SearchResultsOverlay: React.FC<SearchResultsOverlayProps> = ({
  isVisible,
  searchValue,
  navHeight,
  onResultClick,
}) => {
  const t = useTranslations("nav.search");
  const [activeTab, setActiveTab] = useState<"products" | "vendors">(
    "products",
  );

  // F17: reduced-motion gate — opacity-only variants for users who prefer reduced motion
  const shouldReduceMotion = useReducedMotion();
  const overlayVariants = useMemo(
    () =>
      shouldReduceMotion
        ? { hidden: { opacity: 1 }, visible: { opacity: 1 } }
        : SEARCH_OVERLAY_VARIANTS,
    [shouldReduceMotion],
  );

  // Search API call for products
  const productsSearchResults = $api.useQuery(
    "get",
    "/search",
    {
      params: {
        query: {
          name:
            searchValue.length >= SEARCH_MIN_CHARS ? searchValue : undefined,
          type: "product",
        },
      },
    },
    {
      enabled:
        searchValue.length >= SEARCH_MIN_CHARS && activeTab === "products",
    },
  );

  // Search API call for vendors
  const vendorsSearchResults = $api.useQuery(
    "get",
    "/search",
    {
      params: {
        query: {
          name:
            searchValue.length >= SEARCH_MIN_CHARS ? searchValue : undefined,
          type: "vendor",
        },
      },
    },
    {
      enabled:
        searchValue.length >= SEARCH_MIN_CHARS && activeTab === "vendors",
    },
  );

  if (!isVisible) return null;

  return (
    <motion.div
      className="fixed inset-x-0 bottom-0 bg-bg-primary z-2000 overflow-y-auto"
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      transition={SEARCH_MOTION_TRANSITION}
      style={{
        top: `${navHeight}px`,
        height: `calc(100vh - ${navHeight}px)`,
      }}
    >
      <Tabs
        syncWithHash={false}
        value={activeTab}
        onValueChange={(value) => {
          if (value === "products" || value === "vendors") {
            setActiveTab(value);
          }
        }}
        defaultValue="products"
        className="h-full flex flex-col"
      >
        <div className="px-4 pt-4 flex justify-start">
          <TabsList variant="bordered" className="w-fit">
            <TabsTrigger variant="bordered" value="products" className="flex-1">
              {t("productsTab")}
            </TabsTrigger>
            <TabsTrigger variant="bordered" value="vendors" className="flex-1">
              {t("shopsTab")}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="products" className="flex-1 overflow-y-auto mt-0">
          {productsSearchResults.isLoading ? (
            <SearchResultSkeleton />
          ) : (
            <ProductSearchResults
              products={productsSearchResults.data?.data.products}
              searchValue={searchValue}
              onResultClick={onResultClick}
            />
          )}
        </TabsContent>

        <TabsContent value="vendors" className="flex-1 overflow-y-auto mt-0">
          {vendorsSearchResults.isLoading ? (
            <SearchResultSkeleton />
          ) : (
            <VendorSearchResults
              vendors={vendorsSearchResults.data?.data.vendors}
              onResultClick={onResultClick}
            />
          )}
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};
